import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const RationalThoughtNumberCalculator = () => {
  const [name, setName] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  // Helper function to convert letters to numbers
  const letterToNumber = (letter) => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return letters.indexOf(letter) + 1;
  };

  // Helper function to reduce numbers to single digit
  const reduceNumber = (num) => {
    while (num > 9 && ![11, 22, 33].includes(num)) {
      num = num.toString().split('').reduce((sum, digit) => sum + +digit, 0);
    }
    return num;
  };

  // Helper function to calculate Rational Thought Numbers
  const calculateRationalThoughtNumbers = (inputName) => {
    const name = inputName.toUpperCase();
    const letters = name.split('');
    const rationalNumbers = [];
    
    // Calculate rational thought numbers for each letter
    for (let i = 0; i < letters.length; i++) {
      if (letters[i] !== ' ') {
        const num = letterToNumber(letters[i]);
        const reduced = reduceNumber(num);
        rationalNumbers.push({
          letter: letters[i],
          number: reduced
        });
      }
    }

    // Calculate total rational thought number
    const totalRationalNumber = rationalNumbers.reduce((sum, item) => sum + item.number, 0);
    const totalReduced = reduceNumber(totalRationalNumber);

    return {
      rationalNumbers,
      totalRationalNumber,
      totalReduced
    };
  };

  const handleInputChange = (e) => {
    setName(e.target.value);
    setError('');
  };

  const calculateRationalThought = () => {
    if (!name.trim()) {
      setError('Please enter a name');
      return;
    }

    const rationalData = calculateRationalThoughtNumbers(name);
    setResults(rationalData);

    // Prepare chart data
    const rationalChartData = {
      labels: rationalData.rationalNumbers.map(item => item.letter),
      datasets: [{
        label: 'Rational Thought Numbers',
        data: rationalData.rationalNumbers.map(item => item.number),
        backgroundColor: [
          'rgba(153, 102, 255, 0.7)',
          'rgba(255, 159, 64, 0.7)',
          'rgba(75, 192, 192, 0.7)',
          'rgba(255, 99, 132, 0.7)',
          'rgba(54, 162, 235, 0.7)'
        ],
        borderColor: [
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1,
        hoverOffset: 4
      }]
    };

    setChartData(rationalChartData);
  };

  return (
    <div className="math-calculators max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Helmet>
        <title>Rational Thought Number Calculator | Analyze Your Clear Thinking</title>
        <meta name="description" content="Calculate your Rational Thought Numbers using our Numerology Calculator. Understand your clear thinking patterns and decision-making abilities." />
        <meta name="keywords" content="rational thought numbers, numerology calculator, clear thinking, decision making, personal growth" />
      </Helmet>

      <div className="calculator-header mb-12">
        <div className="flex items-center gap-4">
          <div className="header-icon text-4xl">🧠</div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Rational Thought Number Calculator</h1>
            <p className="text-lg text-purple-600 font-semibold">Analyze Your Clear Thinking Patterns</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="calculator-form max-w-2xl mx-auto bg-white rounded-xl shadow-lg p-8"
      >
        <div className="input-group mb-6">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
            Full Name:
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={name}
            onChange={handleInputChange}
            className="appearance-none block w-full bg-gray-200 text-gray-700 border rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
            placeholder="Enter your full name"
          />
        </div>

        <button
          onClick={calculateRationalThought}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white font-bold py-3 px-4 rounded focus:outline-none focus:bg-white"
        >
          Calculate Rational Thought Numbers
        </button>

        {error && (
          <div className="text-red-500 text-center mt-4">
            {error}
          </div>
        )}

        {results && (
          <div className="results-section mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Your Rational Thought Numbers</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h4 className="text-xl font-semibold mb-4">Thought Analysis</h4>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total Thought Number:</span>
                      <span className="font-medium text-purple-600 text-2xl">{results.totalReduced}</span>
                    </div>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <h5 className="font-medium mb-4">Letter Analysis:</h5>
                    <div className="space-y-2">
                      {results.rationalNumbers.map((item, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <span className="text-gray-600">{item.letter}:</span>
                          <span className="font-medium">{item.number}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-semibold mb-4">Thought Visualization</h4>
                <div className="h-96 bg-gray-50 rounded-xl p-6">
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Rational Thought Numbers by Letter',
                          color: '#4F46E5',
                          font: {
                            size: 16
                          }
                        },
                        legend: {
                          display: true,
                          position: 'bottom',
                          labels: {
                            color: '#4B5563',
                            font: {
                              size: 12
                            }
                          }
                        }
                      },
                      cutout: '60%',
                      animation: {
                        animateRotate: true,
                        animateScale: true
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Examples & Interpretations <span className="ml-2 text-2xl">📊</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 1: Analytical Thinker</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: John</p>
                <p className="text-purple-600 text-2xl mb-2">Total Thought: 8</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name shows strong analytical thinking, logical reasoning, and problem-solving abilities.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Example 2: Creative Thinker</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium mb-2">Name: Sarah</p>
                <p className="text-purple-600 text-2xl mb-2">Total Thought: 3</p>
                <p className="mt-4 text-gray-700">
                  Interpretation: This name indicates creative thinking, innovative ideas, and artistic expression.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Thought Number Meanings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-lg font-semibold mb-2">3 - Creative Thinking</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Imagination</li>
                    <li>Innovation</li>
                    <li>Artistic expression</li>
                    <li>Original ideas</li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-lg font-semibold mb-2">8 - Analytical Thinking</h4>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Logical reasoning</li>
                    <li>Problem-solving</li>
                    <li>Decision making</li>
                    <li>Strategic planning</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          How to Use <span className="ml-2 text-2xl">📋</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <p className="text-gray-700 mb-6">
            Follow these steps to calculate your Rational Thought Numbers:
          </p>
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 1: Enter Your Name</h3>
              <p className="text-gray-700">
                Input your full name in the input field.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 2: Calculate</h3>
              <p className="text-gray-700">
                Click the "Calculate Rational Thought Numbers" button to see your results.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Step 3: Understand Results</h3>
              <p className="text-gray-700">
                Review your total thought number and letter analysis to understand your thinking patterns.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          FAQ <span className="ml-2 text-2xl">❓</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold mb-2">What are Rational Thought Numbers?</h3>
              <p className="text-gray-700">
                Rational Thought Numbers in numerology represent your thinking patterns and decision-making abilities. They help identify how you process information and make decisions.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">How are they calculated?</h3>
              <p className="text-gray-700">
                Each letter in your name is assigned a number (A=1, B=2, etc.), and these numbers are reduced to single digits to reveal your thinking patterns.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">What do they reveal?</h3>
              <p className="text-gray-700">
                They show your natural thinking style, problem-solving approach, and decision-making tendencies, helping you understand your cognitive strengths and areas for improvement.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="calculator-section mt-24">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          Features & Benefits <span className="ml-2 text-2xl">✨</span>
        </h2>
        <div className="bg-white p-8 rounded-xl shadow-md">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Key Features</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Accurate Thought Number calculation</li>
                <li>Letter analysis</li>
                <li>Visual representation</li>
                <li>Detailed interpretation</li>
                <li>Personalized insights</li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-purple-600 mb-4">Benefits</h3>
              <ul className="list-disc pl-8 space-y-3">
                <li>Understand your thinking patterns</li>
                <li>Improve decision-making</li>
                <li>Enhance problem-solving</li>
                <li>Develop cognitive strengths</li>
                <li>Support personal growth</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RationalThoughtNumberCalculator;
